import {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import {
  type TextControlBaseInstance,
  TextControlBaseSlot
} from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import useMergedRef from '@react-hook/merged-ref';
import { type CountryCode, getCountryCallingCode } from 'libphonenumber-js/max';
import { ShortText, type ShortTextProps } from '../../short-text';
import { usePhoneMask } from '../hooks';
import type { CountryChangeMetaRef } from '../phone';
import type { Country } from '../types';
import {
  formatPhonePrefix,
  getFormattedPhone,
  getNewCursorPosition,
  isEmptyOrRemovedPlusPrefix,
  isPhonePossible,
  type PhoneCountryDetector,
  prependPlusIfNeeded,
  restoreCursor
} from '../utility';
import { FlagDropdownTrigger } from './FlagDropdownTrigger';

interface PhoneInputProps {
  toggleDropdown: () => void;
  isOpen: boolean;
  selectedCountry: Country;
  placeholderChar?: string;
  countryChangeMetaRef: MutableRefObject<CountryChangeMetaRef>;
  flagContainerRef: MutableRefObject<HTMLButtonElement | null>;
  onDetectedCountryChange: (countryCode: CountryCode) => void;
  showPlaceholder: boolean;
  fieldColor: string;
  fieldFillColor: string;
  fieldBorderType: string | null;
  fieldBorderRadius: number;
  countryDetector: PhoneCountryDetector;
}

export const PhoneInput = forwardRef<
  TextControlBaseInstance,
  PhoneInputProps & Partial<ShortTextProps>
>(
  (
    {
      toggleDropdown,
      isOpen,
      selectedCountry,
      placeholderChar = '0',
      onDetectedCountryChange,
      fieldColor,
      fieldFontStyle,
      fieldFillColor,
      fieldBorderType,
      fieldLabelPosition,
      fieldBorderRadius,
      countryChangeMetaRef,
      flagContainerRef,
      showPlaceholder,
      countryDetector,
      onChange: validatorOnChange,
      onBlur,
      onFocus,
      value: externalValue,
      ...ShortTextProps
    },
    forwardingRef
  ) => {
    const { t } = useLingui();
    const { countryCode, iconSrc } = selectedCountry;
    const callingCode = getCountryCallingCode(countryCode);
    const [value, setValue] = useState(
      externalValue ?? formatPhonePrefix(callingCode)
    );
    const [focused, setFocused] = useState(false);
    const [flagTriggerFocused, setFlagTriggerFocused] = useState(false);

    useEffect(() => {
      if (externalValue) {
        const normalizedValue = externalValue.startsWith('+')
          ? externalValue
          : '+' + externalValue;
        setValue(getFormattedPhone(normalizedValue, countryCode));
      }
      // oxlint-disable-next-line exhaustive-deps
    }, [externalValue]);

    const internalRef = useRef<TextControlBaseInstance | null>(null);
    const combinedRef = useMergedRef(forwardingRef, internalRef);

    const specialEmptyCase = '+' + callingCode;
    const fieldLabelInside =
      fieldLabelPosition === 'inside' &&
      !focused &&
      !flagTriggerFocused &&
      (value === '' || value === specialEmptyCase) &&
      !isOpen;

    const mask = usePhoneMask({
      countryCode,
      placeholderChar,
      showPlaceholder,
      fieldLabelInside,
      value
    });

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
        setFocused(false);

        onBlur?.(event);
      },
      [onBlur]
    );

    const handleFocus = useCallback(
      (event?: FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
        setFocused(true);

        onFocus?.(event);
      },
      [onFocus]
    );

    const updateCountry = useCallback(() => {
      const newCountryCode = countryDetector.getCountryCode();
      if (!newCountryCode || newCountryCode === countryCode) {
        return;
      }

      onDetectedCountryChange(newCountryCode);
    }, [onDetectedCountryChange, countryCode]);

    useEffect(() => {
      let requestAnimationFrameId = null;
      if (countryChangeMetaRef.current.changedByCountryList) {
        requestAnimationFrameId = requestAnimationFrame(() => {
          internalRef.current?.focusInput();
        });
      }

      const newCallingCode = getCountryCallingCode(selectedCountry.countryCode);

      const shouldUseExternalVal =
        externalValue &&
        !countryChangeMetaRef.current.changedByCountryList &&
        !countryChangeMetaRef.current.changedByConfigurator;
      const formatted = shouldUseExternalVal
        ? getFormattedPhone(externalValue, countryCode)
        : formatPhonePrefix(newCallingCode);

      setValue(formatted);

      countryChangeMetaRef.current.changedByCountryList = false;
      countryChangeMetaRef.current.changedByConfigurator = false;

      countryDetector.reset();

      return () => {
        if (requestAnimationFrameId) {
          cancelAnimationFrame(requestAnimationFrameId);
        }
      };
      // oxlint-disable-next-line exhaustive-deps
    }, [selectedCountry, countryDetector]);

    useEffect(() => {
      return countryDetector.subscribe(updateCountry);
    }, [updateCountry, countryDetector]);

    const prevRawRef = useRef<string>('');
    const handleChange = useCallback(
      (
        value: string,
        event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => {
        const previousRawValue = prevRawRef.current ?? '';
        const inputElement = internalRef.current?.inputRef.current;

        const { currentInput: rawInput, cursorOffset } = prependPlusIfNeeded(
          value,
          previousRawValue
        );

        countryChangeMetaRef.current.changedByCountryList = false;
        countryChangeMetaRef.current.changedByConfigurator = false;

        const trimmedValue = rawInput.trim();

        countryDetector.input(trimmedValue);

        const currentCursorPosition =
          typeof inputElement?.selectionStart === 'number'
            ? inputElement.selectionStart + cursorOffset
            : rawInput.length;

        if (isEmptyOrRemovedPlusPrefix(rawInput, previousRawValue)) {
          validatorOnChange?.(trimmedValue, event);
          setValue(rawInput);
          restoreCursor(inputElement, currentCursorPosition);
          prevRawRef.current = rawInput;
          return;
        }

        const formattedInput = getFormattedPhone(rawInput, countryCode);

        if (!isPhonePossible(formattedInput)) {
          return;
        }

        const newCursorPosition = getNewCursorPosition({
          rawInput,
          formattedInput,
          rawCursorIndex: currentCursorPosition
        });

        validatorOnChange?.(trimmedValue, event);
        setValue(formattedInput);
        restoreCursor(inputElement, newCursorPosition);

        prevRawRef.current = rawInput;
      },
      [validatorOnChange, countryCode, countryDetector]
    );

    const StableFlagDropdownTrigger = useMemo(
      () => (
        <FlagDropdownTrigger
          ariaLabel={t`Toggle country selection`}
          fieldColor={fieldColor}
          isHidden={fieldLabelInside}
          flagContainerRef={flagContainerRef}
          countryIconSrc={iconSrc}
          open={isOpen}
          onClick={toggleDropdown}
          onFocus={() => setFlagTriggerFocused(true)}
          onBlur={() => setFlagTriggerFocused(false)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') {
              return;
            }
            event.preventDefault();
            toggleDropdown();
          }}
        />
      ),
      [
        iconSrc,
        t,
        isOpen,
        fieldColor,
        fieldLabelInside,
        toggleDropdown,
        flagContainerRef
      ]
    );

    return (
      <Container _fieldLabelInside={fieldLabelInside}>
        <ShortText
          {...ShortTextProps}
          leftSlot={StableFlagDropdownTrigger}
          mask={mask}
          forceFocusAnimation={isOpen}
          fieldLabelPosition={fieldLabelPosition}
          fieldColor={fieldColor}
          fieldFillColor={fieldFillColor}
          fieldBorderType={fieldBorderType}
          fieldFontStyle={fieldFontStyle}
          fieldBorderRadius={fieldBorderRadius}
          shouldFocusSlotOnClick={false}
          specialEmptyCase={specialEmptyCase}
          value={value}
          ref={combinedRef}
          type="tel"
          inputMode="numeric"
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
      </Container>
    );
  }
);

const Container = styled.div<{ _fieldLabelInside: boolean }>`
  ${({ _fieldLabelInside }) =>
    _fieldLabelInside &&
    `
      & div${TextControlBaseSlot} {
        margin: 0;
      }
    `}
`;
